import { Modal, Stack, Text, TextInput } from "@mantine/core"
import { useForm, zodResolver } from "@mantine/form"
import { notifications } from "@mantine/notifications"
import { Button, MultiSelect } from "@tfg/ui"
import { AxiosError } from "axios"

import { OrganizationsResponses } from "@tfg/types/endpoints/organizations"
import {
  createDisplaySettingSchema,
  createDisplaySettingValidationType,
} from "@tfg/types/schemas/displaySettings"

import { FiltersForm } from "~/pages/Brand/OrganizationSettings/ProductPages/DisplaySettingsCrud/FiltersForm/FiltersForm.tsx"
import {
  useCreateDisplaySettingsMutation,
  useUpdateDisplaySettingsMutation,
} from "~/pages/Brand/OrganizationSettings/ProductPages/hooks.ts"

interface ModalProps {
  opened: boolean
  close: () => void
  organizationSettingsId: number
  organizationId: number
  displaySetting?: OrganizationsResponses.Organization["settings"]["displaySettings"][number]
}
export const CreateOrUpdateDisplaySettingsModal = ({
  opened,
  close,
  organizationSettingsId,
  organizationId,
  displaySetting,
}: ModalProps) => {
  const form = useForm<createDisplaySettingValidationType>({
    validate: zodResolver(createDisplaySettingSchema),
    initialValues: {
      id: displaySetting?.id ?? "",
      organizationSettingsId,
      searchEngine: displaySetting?.searchEngine ?? false,
      agecProductPage: displaySetting?.agecProductPage ?? false,
      slug: displaySetting?.slug ?? "",
      filters: {},
    },
  })

  const mutationOptions = {
    onError: (error: AxiosError) => {
      notifications.show({
        title: "Error",
        message: error.message,
        color: "red",
      })
    },
    onSuccess: () => {
      close()
      form.reset()
    },
  }

  const { mutate: create, isLoading: isCreating } =
    useCreateDisplaySettingsMutation(organizationId, mutationOptions)
  const { mutate: update, isLoading: isUpdating } =
    useUpdateDisplaySettingsMutation(organizationId, mutationOptions)

  const mutate = displaySetting ? update : create
  const isLoading = displaySetting ? isUpdating : isCreating

  return (
    <Modal
      size="lg"
      opened={opened}
      onClose={() => {
        close()
        form.reset()
      }}
      title="Create Display Setting"
    >
      <form
        onSubmit={form.onSubmit((values) => {
          mutate(values)
        })}
      >
        <Stack>
          <TextInput
            label="Name"
            placeholder="Name"
            required
            {...form.getInputProps("slug")}
          />
          <MultiSelect
            label="Apply settings to"
            data={[
              { value: "searchEngine", label: "Search Engine" },
              { value: "agecProductPage", label: "Agec Product Page" },
            ]}
            value={[
              form.values.searchEngine ? "searchEngine" : null,
              form.values.agecProductPage ? "agecProductPage" : null,
            ].filter((value): value is string => !!value)}
            onChange={(value) => {
              form.setFieldValue("searchEngine", value.includes("searchEngine"))
              form.setFieldValue(
                "agecProductPage",
                value.includes("agecProductPage")
              )
            }}
          />
          <Text>Filters to apply setting to:</Text>
          <Text>{JSON.stringify(form.values.filters)}</Text>

          <FiltersForm
            filters={form.values.filters}
            setFiltersInForm={(filters) =>
              form.setFieldValue("filters", filters)
            }
          />

          <Button type="submit" disabled={!form.isValid()} loading={isLoading}>
            Submit
          </Button>
        </Stack>
      </form>
    </Modal>
  )
}
